import { markRaw, reactive, shallowReactive } from 'vue';
import { createSharedComposable } from '@vueuse/core';

function _useOverlay() {
    const overlays = shallowReactive([]);

    const create = (component, _options = undefined) => {
        const { props: props, defaultOpen, destroyOnClose } = _options || {};

        const options = reactive({
            id: Symbol(import.meta.dev ? 'useOverlay' : ''),
            isOpen: !!defaultOpen,
            component: markRaw(component),
            isMounted: !!defaultOpen,
            destroyOnClose: !!destroyOnClose,
            props: props || {},
        });

        overlays.push(options);

        return {
            ...options,
            open: (props = undefined) => open(options.id, props),
            close: value => close(options.id, value),
            patch: (props) => patch(options.id, props),
        };
    };

    const open = (id, props = undefined) => {
        const overlay = getOverlay(id);

        // If props are provided, update the overlay's props
        if (props) {
            patch(overlay.id, props);
        }

        // Set isOpen before isMounted so the component mounts with show=true
        // on the very first render. Combined with TransitionRoot's `appear`,
        // this guarantees the enter transition runs every time.
        overlay.isOpen = true;
        overlay.isMounted = true;

        return {
            id,
            isMounted: overlay.isMounted,
            isOpen: overlay.isOpen,
            result: new Promise(resolve => overlay.resolvePromise = resolve),
        };
    };

    const close = (id, value = undefined) => {
        const overlay = getOverlay(id);

        overlay.isOpen = false;

        // Resolve the promise if it exists
        if (overlay.resolvePromise) {
            overlay.resolvePromise(value);
            overlay.resolvePromise = undefined;
        }
    };

    const closeAll = () => {
        overlays.forEach(overlay => close(overlay.id));
    };

    const unMount = (id) => {
        const overlay = getOverlay(id);

        overlay.isMounted = false;

        if (overlay.destroyOnClose) {
            const index = overlays.findIndex(overlay => overlay.id === id);
            overlays.splice(index, 1);
        }
    };

    const patch = (id, props) => {
        const overlay = getOverlay(id);

        Object.entries(props).forEach(([key, value]) => {
            (overlay.props)[key] = value;
        });
    };

    const getOverlay = (id) => {
        const overlay = overlays.find(overlay => overlay.id === id);

        if (!overlay) {
            throw new Error('Overlay not found');
        }

        return overlay;
    };

    const isOpen = (id) => {
        const overlay = getOverlay(id);

        return overlay.isOpen;
    };

    return {
        overlays,
        open,
        close,
        closeAll,
        create,
        patch,
        unMount,
        isOpen,
    };
}

export const useOverlay = /* @__PURE__ */ createSharedComposable(_useOverlay);
